import { AxiosRequestConfig } from 'axios';

import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { logger } from '@common/logging/logger';
import { AxioClient } from '@/modules/api/AxioClient';

type HttpRequestConfig = Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>;

type RequestOptions = {
  requiredErrorMessage?: string;
  config?: HttpRequestConfig;
  requestKey?: string;
  skipLatest?: boolean;
};

type RequiredRequestOptions = RequestOptions & {
  requiredErrorMessage: string;
};

export abstract class BaseConnector {
  private readonly baseUrl: string;
  private readonly scopes: string[];
  private readonly timeout: number;
  private controllers = new Map<string, AbortController>();

  protected constructor(baseUrl: string, scopes: string[], timeout: number) {
    this.baseUrl = baseUrl;
    this.scopes = scopes;
    this.timeout = timeout;
  }

  protected post<T, D = unknown>(
    endpoint: string,
    data: D,
    options: RequiredRequestOptions,
  ): Promise<T>;
  protected post<T, D = unknown>(
    endpoint: string,
    data?: D,
    options?: RequestOptions,
  ): Promise<T | undefined>;
  protected async post<T, D = unknown>(
    endpoint: string,
    data?: D,
    options?: RequestOptions,
  ): Promise<T | undefined> {
    try {
      const response = options?.skipLatest
        ? await this.getClient().post<T>(endpoint, data, options?.config)
        : await this.runLatest(options?.requestKey ?? `post:${endpoint}`, (signal) =>
            this.getClient().post<T>(endpoint, data, {
              ...options?.config,
              signal: options?.config?.signal ?? signal,
            }),
          );
      return options?.requiredErrorMessage
        ? this.requireResult(response.data, options.requiredErrorMessage)
        : response.data;
    } catch (error: unknown) {
      if (this.isCanceled(error)) {
        logger.debug('POST request was canceled:', error);
        return undefined;
      }
      this.handleHttpError('POST', endpoint, error, options?.requiredErrorMessage);
      return undefined;
    }
  }

  protected get<T>(endpoint: string, options: RequiredRequestOptions): Promise<T>;
  protected get<T>(endpoint: string, options?: RequestOptions): Promise<T | undefined>;
  protected async get<T>(endpoint: string, options?: RequestOptions): Promise<T | undefined> {
    try {
      const response = options?.skipLatest
        ? await this.getClient().get<T>(endpoint, options?.config)
        : await this.runLatest(options?.requestKey ?? `get:${endpoint}`, (signal) =>
            this.getClient().get<T>(endpoint, {
              ...options?.config,
              signal: options?.config?.signal ?? signal,
            }),
          );
      return options?.requiredErrorMessage
        ? this.requireResult(response.data, options.requiredErrorMessage)
        : response.data;
    } catch (error: unknown) {
      if (this.isCanceled(error)) {
        logger.debug('GET request was canceled:', error);
        return undefined;
      }
      this.handleHttpError('GET', endpoint, error, options?.requiredErrorMessage);
      return undefined;
    }
  }

  protected put<T, D = unknown>(
    endpoint: string,
    data: D,
    options: RequiredRequestOptions,
  ): Promise<T>;
  protected put<T, D = unknown>(
    endpoint: string,
    data?: D,
    options?: RequestOptions,
  ): Promise<T | undefined>;
  protected async put<T, D = unknown>(
    endpoint: string,
    data?: D,
    options?: RequestOptions,
  ): Promise<T | undefined> {
    try {
      const response = options?.skipLatest
        ? await this.getClient().put<T>(endpoint, data, options?.config)
        : await this.runLatest(options?.requestKey ?? `put:${endpoint}`, (signal) =>
            this.getClient().put<T>(endpoint, data, {
              ...options?.config,
              signal: options?.config?.signal ?? signal,
            }),
          );
      return options?.requiredErrorMessage
        ? this.requireResult(response.data, options.requiredErrorMessage)
        : response.data;
    } catch (error: unknown) {
      if (this.isCanceled(error)) {
        logger.debug('PUT request was canceled:', error);
        return undefined;
      }
      this.handleHttpError('PUT', endpoint, error, options?.requiredErrorMessage);
      return undefined;
    }
  }

  protected delete<T>(endpoint: string, options: RequiredRequestOptions): Promise<T>;
  protected delete<T>(endpoint: string, options?: RequestOptions): Promise<T | undefined>;
  protected async delete<T>(endpoint: string, options?: RequestOptions): Promise<T | undefined> {
    try {
      const response = options?.skipLatest
        ? await this.getClient().delete<T>(endpoint, options?.config)
        : await this.runLatest(options?.requestKey ?? `delete:${endpoint}`, (signal) =>
            this.getClient().delete<T>(endpoint, {
              ...options?.config,
              signal: options?.config?.signal ?? signal,
            }),
          );
      return options?.requiredErrorMessage
        ? this.requireResult(response.data, options.requiredErrorMessage)
        : response.data;
    } catch (error: unknown) {
      if (this.isCanceled(error)) {
        logger.debug('DELETE request was canceled:', error);
        return undefined;
      }
      this.handleHttpError('DELETE', endpoint, error, options?.requiredErrorMessage);
      return undefined;
    }
  }

  //
  // Utility methods
  //

  private requireResult<T>(result: T | undefined, errorMessage: string): T {
    if (result === undefined) {
      throw new Error(errorMessage);
    }
    return result;
  }

  private async runLatest<T>(
    requestKey: string,
    request: (signal: AbortSignal) => Promise<T>,
  ): Promise<T> {
    const signal = this.nextSignal(requestKey);

    try {
      return await request(signal);
    } finally {
      this.complete(requestKey, signal);
    }
  }

  private isCanceled(error: unknown): boolean {
    if (error instanceof DOMException && error.name === 'AbortError') {
      logger.debug('Request was aborted:', error.message);
      return true;
    }

    if (error instanceof Error && error.name === 'CanceledError') {
      logger.debug('Request was cancelled:', error.message);
      return true;
    }

    return false;
  }

  private handleHttpError(
    method: string,
    endpoint: string,
    error: unknown,
    requiredErrorMessage?: string,
  ) {
    logger.error(`HTTP ${method} ${this.baseUrl}${endpoint} failed:`, error);

    if (requiredErrorMessage) {
      SnackbarServiceFactory.getInstance()
        .createSnackbarService()
        .showMessage(requiredErrorMessage);
    }
  }

  private getClient() {
    return AxioClient.api(this.baseUrl, this.scopes, this.timeout);
  }

  private nextSignal(requestKey: string): AbortSignal {
    this.cancel(requestKey);

    const controller = new AbortController();
    this.controllers.set(requestKey, controller);

    return controller.signal;
  }

  private complete(requestKey: string, signal: AbortSignal): void {
    const controller = this.controllers.get(requestKey);

    if (!controller) {
      return;
    }

    if (controller.signal === signal) {
      this.controllers.delete(requestKey);
    }
  }

  private cancel(requestKey: string): void {
    const controller = this.controllers.get(requestKey);

    if (!controller) {
      return;
    }

    controller.abort();
    this.controllers.delete(requestKey);
  }
}
